import { ApiCart, VLPVariant } from '@storefronts/common-types'
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
    addCartLineItems,
    AddCartLineItemsResponse,
    getCart,
    initializeCart,
    removeCartLineItems,
    RemoveCartLineItemsResponse,
    updateCartLineItems,
    UpdateCartLineItemsResponse,
    upsertCart,
} from '../../api'
import { EL_USER_DATA } from '../analytics/elevar'
import { trackCartQuantityDeltaIfNeeded } from '../analytics/cartAnalytics'
import { AttentiveTracker } from '../components/pixels/AttentivePixel'
import { BlotoutTrackker } from '../components/pixels/BlotoutPixel'
import { ConvergeTracker } from '../components/pixels/ConvergePixel'
import { PostscriptTracker } from '../components/pixels/PostscriptPixel'
import { TripleWhaleTracker } from '../components/pixels/TripleWhalePixel'
import {
    getFromStorageParsed,
    setCartInStorage,
    setInStorage,
} from '../storage'
import {
    AjaxShopifyCartItem,
    Cart,
    CartItem,
    CartItemStatus,
    isAjaxShopifyCartData,
    isApiCart,
    VariantWithImage,
} from '../types'
import { urlWithQueryParams } from '../utils/helpers'
import { applyShopPayToCheckoutUrl } from '../utils/checkoutPayment'
import { AnalyticsContext } from './analyticsContext'
import { CartContext } from './cartContext'
import { LandingPageLinksContext } from './LandingPageLinksContext'
import { ViddyLPContext } from './viddyLPContext'
import { UserEventDataContext } from './UserEventDataContextProvider'
import {
    IS_EDITOR_PREVIEW,
    IS_NO_PIXELS,
    QueryParamContext,
} from './QueryParamContextProvider'
import {
    convertAjaxToVariant,
    findVariantWithImageForAjaxCartItem,
} from '../utils/userEventTools'
import {
    convertApiCartToCart,
    syncCartWithResponse,
    getDiscountedCartTotal,
    getEffectiveFixedDiscountForCart,
    getUnDiscountedCartTotal,
    setCartItemStatus,
    clearCartItemStatus,
    hasDeltas,
    mergeDeferredAddItems,
    getErrorMessage,
    setRecoveredCartInSharedCart,
    isRecoveredCartInSharedCart,
    areMatchingCartItems,
    getCheckoutUrl,
    hasShopifyCartLineId,
    findCartItem,
    partitionCartItemsForShopifySync,
} from '../utils/cartTools'
import { SimpleAsyncTaskQueue } from '../utils/simpleAsyncTaskQueue'

const taskQueue = new SimpleAsyncTaskQueue(5000)

// TODO: Determine if we can remove a lot of useEffects for all of the initialization:
//  * cart initialization
//    * depends on context
//  * cart recovery
//    * depends on context
//    * depends on cart initialization
//    * depends on deferred cart actions
// Deferred adds are applied when initializeCart resolves, not from an effect.
// TODO: Further cleanups once the CartProvider is removed.
// Do we want to continue saving the Cart to cookies/localStorage? Right now we do
// it in order to support being able to switch between cartProviders without losing
// the cart contents.
export const ShopifyCartProvider: React.FC<{
    children: React.ReactNode
    switchToFallbackCartProvider: (
        error: Error,
        isWarn?: boolean,
        details?: string,
    ) => void
}> = ({ children, switchToFallbackCartProvider }) => {
    const [cart, setCart] = useState<Cart>({
        items: [],
        id: uuidv4(),
        isLoading: false,
    })
    const { landingPage, viddyId, discountCode } = useContext(ViddyLPContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { abandonmentStorefrontData, initialized } =
        useContext(UserEventDataContext)
    const { EVENT_ID, P_EVENT_ID, SHOP, SHARE_CART } =
        useContext(QueryParamContext)
    const CART_RECOVERED_KEY = useMemo(
        () => `__vd_cart_rec__${landingPage.landingPageHash}`,
        [landingPage.landingPageHash],
    )
    const [cartRecovered, setCartRecovered] = useState<boolean>(false)
    const [deferedAddItems, setDeferredAddItems] = useState<CartItem[]>([])

    const { resolvedLandingPageLinks } = useContext(LandingPageLinksContext)
    const resolvedLandingPageLinksRef = useRef(resolvedLandingPageLinks)
    resolvedLandingPageLinksRef.current = resolvedLandingPageLinks

    // Assigned during render. useRef(cart) would close the initializer over state.
    // initializeCart also writes the ready cart here before the next render.
    // In-flight adds (Buy Now awaits a variant lookup) still close over the
    // render where cart.isLoading was true, so they must read this ref.
    const cartRef = useRef<Cart | null>(null)
    cartRef.current = cart

    const deferredAddsRef = useRef<{
        items: CartItem[]
        immediateCheckout: boolean
        shopPay: boolean
    }>({ items: [], immediateCheckout: false, shopPay: false })
    // Assigned once addCartItems exists. Init and recovery outlive the render
    // that started them, so completion calls the latest function.
    const flushDeferredAddsRef = useRef<(readyCart: Cart) => void>(() => {})
    const tryRecoverCartRef = useRef<() => void>(() => {})

    const deferCartItems = (
        incoming: CartItem[],
        immediateCheckout: boolean,
        shopPay?: boolean,
    ) => {
        const prev = deferredAddsRef.current
        const items = incoming.reduce(
            (acc, item) => mergeDeferredAddItems(acc, item),
            prev.items,
        )
        deferredAddsRef.current = {
            items,
            immediateCheckout: prev.immediateCheckout || immediateCheckout,
            shopPay: immediateCheckout ? (shopPay ?? false) : prev.shopPay,
        }
        setDeferredAddItems(items)
    }

    const convertApiCartToCartCallback = useCallback(
        (recoveredCart: ApiCart) => {
            return convertApiCartToCart(
                recoveredCart,
                resolvedLandingPageLinksRef.current,
                landingPage.discount,
            )
        },
        [landingPage.discount],
    )

    useEffect(() => {
        if (IS_EDITOR_PREVIEW() || IS_NO_PIXELS()) {
            // Preview (editor or no-pixels iframe): skip server-side cart init.
            // Cart UI works client-side only, and we avoid a Shopify cart 401
            // triggering switchToFallbackCartProvider -> router.push, which would
            // reload the whole landing page inside the preview iframe.
            setCart((prevCart) => ({ ...prevCart, isLoading: false }))
            return
        }

        if (viddyId && landingPage.client.clientHash) {
            const eventParams =
                EVENT_ID || P_EVENT_ID
                    ? {
                          shop: SHOP ?? landingPage.client.shopifyShop,
                          eventId: EVENT_ID,
                          pEventId: P_EVENT_ID,
                      }
                    : undefined

            const discountCodes = discountCode ? [discountCode] : undefined

            setCart((prevCart) => ({ ...prevCart, isLoading: true }))
            initializeCart(
                landingPage.client,
                viddyId,
                landingPage.landingPageHash,
                discountCodes,
                eventParams,
                SHARE_CART,
            )
                .then((resp) => {
                    if (resp.isError) {
                        // TODO: Narrow which errors require switching to a default cart provider, or do something else?
                        switchToFallbackCartProvider(
                            new Error(getErrorMessage(resp)),
                            resp.warn,
                            `isError response from initializeCart - cartUuid: ${viddyId}`,
                        )
                        return
                    }
                    const newCart = convertApiCartToCartCallback(resp.result)
                    newCart.isLoading = false
                    setCart(newCart)
                    EL_USER_DATA(newCart)
                    setCartInStorage(newCart, landingPage)
                    flushDeferredAddsRef.current(newCart)
                })
                .catch((error) => {
                    // TODO: Narrow which errors require switching to a default cart provider, or do something else?
                    switchToFallbackCartProvider(
                        error as Error,
                        false,
                        `Error thrown in initializeCart - cartUuid: ${viddyId}`,
                    )
                })
        }
    }, [
        viddyId,
        landingPage.client,
        landingPage.landingPageHash,
        landingPage,
        discountCode,
        EVENT_ID,
        P_EVENT_ID,
        SHOP,
        SHARE_CART,
        convertApiCartToCartCallback,
    ])

    const findVariantWithImageForAjaxCartItemCallback = useCallback(
        (item: AjaxShopifyCartItem): VariantWithImage | undefined => {
            return findVariantWithImageForAjaxCartItem(
                item,
                resolvedLandingPageLinks,
                landingPage.discount,
            )
        },
        [resolvedLandingPageLinks, landingPage.discount],
    )

    const convertAjaxToVariantCallback = useCallback(
        (item: AjaxShopifyCartItem): VariantWithImage => {
            return convertAjaxToVariant(item, landingPage.discount)
        },
        [landingPage.discount],
    )

    useEffect(() => {
        tryRecoverCartRef.current()
    }, [
        // TODO: Fix dependencies
        abandonmentStorefrontData?.recover_cart_data,
        initialized,
        cartRecovered,
        cart.isLoading,
        cart.id,
        landingPage.client.clientHash,
        findVariantWithImageForAjaxCartItemCallback,
        convertAjaxToVariantCallback,
        convertApiCartToCartCallback,
        deferedAddItems,
        CART_RECOVERED_KEY,
        landingPage,
    ])

    const cloneCart = (cart: Cart) => {
        return JSON.parse(JSON.stringify(cart)) as Cart
    }

    type CartEndpointFunction = () => Promise<
        | AddCartLineItemsResponse
        | UpdateCartLineItemsResponse
        | RemoveCartLineItemsResponse
    >

    const queueAddCartLineItemsTask = (
        addedItems: CartItem[],
        immediateCheckout: boolean,
        callback?: (cart: Cart) => void,
        shopPay?: boolean,
    ) => {
        _queueUpdateCartTask(
            { addedItems },
            immediateCheckout,
            () =>
                addCartLineItems(
                    landingPage.client,
                    viddyId,
                    landingPage.landingPageHash,
                    addedItems,
                ),
            'add item(s)',
            // Callback that is called when the cart operation is successful.
            (cart) => {
                // Quick hack to get around issues with sometimes
                // an item is not returned in the cart response
                // even if it was added to the cart.
                if (taskQueue.isEmpty() && addedItems.length > 0) {
                    // No other tasks in the queue, so no follow up operations that will
                    // get a fresh cart response.

                    // Look for items that were not included in the cart response for this operation
                    const addedItemsNotInResponse = cart.items.filter(
                        (item) =>
                            // items without a shopifyLineId are not in any cart response
                            !item.shopifyLineId &&
                            // and were part of this add operation
                            addedItems.some((cartItem) =>
                                areMatchingCartItems(cartItem, item),
                            ),
                    )
                    if (addedItemsNotInResponse.length > 0) {
                        // cart is missing some items, let's queue a task to
                        // fetch the cart.  Use the "addedItemsNotInResponse"
                        // to properly update the cart statuses on the syncCartWithResponse.
                        // The callback will be passed to be called after the we've fetched the cart.
                        queueCartFetchTask(addedItemsNotInResponse, callback)
                        // return early to avoid calling the callback again.
                        return
                    }
                }
                // Call the callback
                callback?.(cart)
            },
            // Callback that is called to setup the `cart` state before
            // the add operation is called.
            (cart) => {
                const updatedCart = cloneCart(cart)
                const newUniqueItems = addedItems.filter(
                    (item) =>
                        !updatedCart.items.some((i: CartItem) =>
                            areMatchingCartItems(i, item),
                        ),
                )
                updatedCart.items = [...newUniqueItems, ...updatedCart.items]
                return updatedCart
            },
            shopPay,
        )
    }

    const queueUpdateCartLineItemsTask = (
        updatedItems: CartItem[],
        immediateCheckout: boolean,
        callback?: (cart: Cart) => void,
        shopPay?: boolean,
    ) => {
        _queueUpdateCartTask(
            { updatedItems },
            immediateCheckout,
            () =>
                updateCartLineItems(
                    landingPage.client,
                    viddyId,
                    landingPage.landingPageHash,
                    updatedItems,
                ),
            'update item(s)',
            callback,
            undefined,
            shopPay,
        )
    }

    const queueRemoveCartLineItemsTask = (
        deletedItems: CartItem[],
        immediateCheckout: boolean,
        callback?: (cart: Cart) => void,
    ) => {
        _queueUpdateCartTask(
            { deletedItems },
            immediateCheckout,
            () =>
                removeCartLineItems(
                    landingPage.client,
                    viddyId,
                    landingPage.landingPageHash,
                    deletedItems,
                ),
            'remove item(s)',
            callback,
        )
    }

    const queueCartFetchTask = (
        addedItems: CartItem[],
        callback?: (cart: Cart) => void,
    ) => {
        _queueUpdateCartTask(
            { addedItems },
            false,
            () =>
                getCart(
                    viddyId,
                    landingPage.client.clientHash,
                    landingPage.landingPageHash,
                ),
            'fetch cart',
            callback,
        )
    }

    const _queueUpdateCartTask = (
        deltas: {
            addedItems?: CartItem[]
            updatedItems?: CartItem[]
            deletedItems?: CartItem[]
        },
        immediateCheckout: boolean,
        updateCartFunction: CartEndpointFunction,
        cartOperation: string = 'unknown',
        callback?: (cart: Cart) => void,
        cartUpdateCallback?: (cart: Cart) => Cart,
        shopPay?: boolean,
    ) => {
        if (
            hasDeltas(deltas) &&
            cartRef.current &&
            !cartRef.current.isLoading
        ) {
            // Include the items being added to the cart.
            setCart((prevCart) => {
                const updatedCart = cartUpdateCallback
                    ? cartUpdateCallback(prevCart)
                    : cloneCart(prevCart)
                setCartItemStatus(updatedCart, deltas)
                return updatedCart
            })

            taskQueue.addTaskToQueue(async () => {
                try {
                    await _updateCart(
                        deltas,
                        immediateCheckout,
                        updateCartFunction,
                        cartOperation,
                        shopPay,
                    )
                    if (callback) {
                        setCart((prevCart) => {
                            callback(prevCart)
                            return prevCart
                        })
                    }
                } catch (error) {
                    console.error('Error in updating cart task', error)
                    setCart((prevCart) => {
                        const newCart = cloneCart(prevCart)
                        clearCartItemStatus(newCart, deltas)
                        callback?.(newCart)
                        return newCart
                    })
                }
            })
        } else if (cartRef.current) {
            callback?.(cartRef.current)
        }
    }

    const _updateCart = async (
        deltas: {
            addedItems?: CartItem[]
            updatedItems?: CartItem[]
            deletedItems?: CartItem[]
        },
        immediateCheckout: boolean,
        updateCartFunction: CartEndpointFunction,
        cartOperation: string,
        shopPay?: boolean,
    ): Promise<void> => {
        // function to handle falling back to the default cart provider
        // with the final desired cart contents.
        function fallbackToDefaultCartProvider(error: Error, details?: string) {
            setCart((prevCart) => {
                const newCart = cloneCart(prevCart)
                // falling back to default cart provider,
                // Since there might be other tasks in the queue,
                // we'll just handle the cart items that have CartItemStatus's set.
                newCart.isLoading = false
                // remove any cartItems that are being deleted
                newCart.items = newCart.items.filter(
                    (item) => item.status !== CartItemStatus.REMOVING,
                )
                // For adding or updating items, they are already in the cart with
                // the correct quantity and selling plan id (if applicable).  We just
                // need to clear the statuses.
                newCart.items.forEach((item) => {
                    item.status = undefined
                })
                setCartInStorage(newCart, landingPage)
                switchToFallbackCartProvider(
                    error,
                    false,
                    `${details ?? ''} - cartUuid: ${viddyId}`,
                )
                return newCart
            })
        }

        try {
            // First step: move this to call the correct API function.
            const response = await updateCartFunction()

            // TBD: Should we do more if there are any errors?
            // By default wthe clearCartItemStatus is called after the catch block.
            // But we may need to do more like remove "addedItems" from the cart.
            if (!response.isError) {
                const responseResult = response.result

                setCart((prevCart) => {
                    const updatedCart = syncCartWithResponse(
                        prevCart,
                        responseResult,
                        landingPage,
                        deltas,
                    )
                    setCartInStorage(updatedCart, landingPage)
                    if (immediateCheckout) {
                        _onCheckout(updatedCart, shopPay)
                    }
                    return updatedCart
                })
            } else {
                // TODO: Narrow which errors require switching to a default cart provider, or do something else?
                fallbackToDefaultCartProvider(
                    new Error(getErrorMessage(response)),
                    `isError response in ${cartOperation} cart operation`,
                )
            }
        } catch (e) {
            // Throw errors?
            // TODO: Narrow which errors require switching to a default cart provider, or do something else?
            fallbackToDefaultCartProvider(
                e as Error,
                `Error thrown in ${cartOperation} cart operation`,
            )
        }
    }

    const _onCheckout = useCallback(
        (anyCart: Cart, shopPay?: boolean) => {
            // TODO: Cleanup some of this logic that was inherited from the CartProvider.
            // Should be expecting the cart id to be present.
            // Shouldn't have a checkout button until the cart has finished initializing.
            const _checkout = (aCart: Cart) => {
                let domain: null | string = null
                if (landingPage.client.mainDomain) {
                    domain = landingPage.client.mainDomain
                } else if (landingPage.client.shopifyShop) {
                    domain = landingPage.client.shopifyShop
                }
                if (!domain) {
                    console.error('No domain found for checkout')
                    return
                }

                const url = getCheckoutUrl(
                    landingPage.client,
                    aCart,
                    discountCode ?? undefined,
                    'ShopifyCartProvider:',
                )

                if (
                    url.includes('.myshopify.com') &&
                    // the selling plan permallink should be on myshopify.com
                    !url.includes('cart/clear')
                ) {
                    const sanitized = urlWithQueryParams(
                        `https://${domain}${url.split('.myshopify.com')[1]}`,
                    )
                    const withShopPay = applyShopPayToCheckoutUrl(
                        sanitized,
                        shopPay,
                    )
                    const href =
                        ConvergeTracker.appendCrossDomainParams(withShopPay)
                    console.log(`ShopifyCartProvider: Checkout URL: ${href}`)
                    window.location.href = href
                } else {
                    const fixedUrl = urlWithQueryParams(url)
                    const withShopPay = applyShopPayToCheckoutUrl(
                        fixedUrl,
                        shopPay,
                    )
                    const href =
                        ConvergeTracker.appendCrossDomainParams(withShopPay)
                    console.log(`ShopifyCartProvider: Checkout URL: ${href}`)
                    window.location.href = href
                }
            }

            const discountCodes = discountCode ? [discountCode] : undefined

            if (anyCart.checkoutUrl) {
                _checkout(anyCart)
                return
            }
            upsertCart(
                anyCart,
                landingPage.client,
                viddyId,
                landingPage.landingPageHash,
                discountCodes,
            )
                .then((resp) => {
                    if (!resp.isError) {
                        setCart((prevCart) => {
                            const newCart = cloneCart(prevCart)
                            newCart.checkoutUrl =
                                resp.result.checkoutUrl ?? newCart.checkoutUrl
                            _checkout(newCart)
                            return newCart
                        })
                        return
                    }
                    _checkout(anyCart)
                })
                .catch((e) => {
                    console.log(e)
                    _checkout(anyCart)
                })
        },
        [landingPage, viddyId, discountCode, SHARE_CART],
    )

    /**
     * A variant can be in the cart twice — once as a one-time purchase and once
     * with a selling plan — so lookups take the plan into account: pass a
     * string/null to match that exact plan (null = no plan), or omit to match
     * any line with the variant.
     */
    const getItem = (
        variant: VLPVariant,
        sellingPlanId?: string | null,
        shopifyLineId?: string,
    ): CartItem | undefined =>
        findCartItem(cartRef.current?.items ?? [], variant, {
            sellingPlanId,
            shopifyLineId,
        })

    const addCartItem = async (
        variant: VariantWithImage,
        quantity: number = 1,
        sellingPlanId?: string | null,
        immediateCheckout: boolean = false,
        shopPay?: boolean,
    ) => {
        if (cartRef.current?.isLoading) {
            deferCartItems(
                [{ variant, quantity, sellingPlanId }],
                immediateCheckout,
                shopPay,
            )
            return
        }
        // Only merge with a line on the same selling plan; adding the same
        // variant with a different plan creates a separate cart line.
        const existingItem = getItem(variant, sellingPlanId ?? null)

        if (existingItem && hasShopifyCartLineId(existingItem)) {
            existingItem.quantity += quantity
            Analytics.TRACK_CART_ADD(variant, quantity)

            queueUpdateCartLineItemsTask(
                [existingItem],
                immediateCheckout,
                undefined,
                shopPay,
            )
        } else {
            const newItem = { variant, quantity, sellingPlanId }
            queueAddCartLineItemsTask(
                [newItem],
                immediateCheckout,
                (updatedCart) => {
                    Analytics.TRACK_CART_ADD(variant, 1)
                    AttentiveTracker.trackAddToCart(variant, cart.id)
                    PostscriptTracker.trackAddToCart(variant, cart.id)
                    TripleWhaleTracker.trackAddToCart(variant, cart.id)
                    ConvergeTracker.trackAddToCart(variant, cart.id)
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    updatedCart && BlotoutTrackker.trackAddToCart(updatedCart)
                },
                shopPay,
            )
        }
    }

    const addCartItems = async (
        variants: {
            variant: VariantWithImage
            quantity?: number
            sellingPlanId?: string | null
            attributes?: { [key: string]: string | null } | undefined
        }[],
        immediateCheckout: boolean = false,
        shopPay?: boolean,
    ) => {
        if (cartRef.current?.isLoading) {
            deferCartItems(
                variants.map(
                    ({ variant, quantity = 1, sellingPlanId, attributes }) => ({
                        variant,
                        quantity,
                        sellingPlanId,
                        attributes,
                    }),
                ),
                immediateCheckout,
                shopPay,
            )
            return
        }

        applyCartItems(variants, immediateCheckout, shopPay)
    }

    const applyCartItems = (
        variants: {
            variant: VariantWithImage
            quantity?: number
            sellingPlanId?: string | null
            attributes?: { [key: string]: string | null } | undefined
        }[],
        immediateCheckout: boolean = false,
        shopPay?: boolean,
    ) => {
        variants.forEach(({ variant, quantity = 1 }) => {
            Analytics.TRACK_CART_ADD(variant, quantity)
        })

        const { addedItems, updatedItems } = partitionCartItemsForShopifySync(
            variants,
            getItem,
        )

        if (addedItems.length > 0) {
            queueAddCartLineItemsTask(
                addedItems,
                immediateCheckout,
                undefined,
                shopPay,
            )
        }
        if (updatedItems.length > 0) {
            queueUpdateCartLineItemsTask(
                updatedItems,
                immediateCheckout,
                undefined,
                shopPay,
            )
        }
    }

    flushDeferredAddsRef.current = (readyCart) => {
        cartRef.current = readyCart
        const pending = deferredAddsRef.current
        if (pending.items.length === 0) return
        deferredAddsRef.current = {
            items: [],
            immediateCheckout: false,
            shopPay: false,
        }
        setDeferredAddItems([])
        applyCartItems(
            pending.items,
            pending.immediateCheckout,
            pending.shopPay,
        )
    }

    tryRecoverCartRef.current = () => {
        const readyCart = cartRef.current
        if (
            !abandonmentStorefrontData?.recover_cart_data ||
            !initialized ||
            cartRecovered ||
            !readyCart ||
            readyCart.isLoading ||
            deferredAddsRef.current.items.length > 0
        ) {
            return
        }

        const setCartAsRecovered = () => {
            setInStorage(CART_RECOVERED_KEY, true)
            setRecoveredCartInSharedCart(
                landingPage.client.clientHash,
                readyCart.id,
                abandonmentStorefrontData?.recover_cart_data ?? null,
            )
            setCartRecovered(true)
        }

        const cartRecoveredFromSharedCart = isRecoveredCartInSharedCart(
            landingPage.client.clientHash,
            readyCart.id,
            abandonmentStorefrontData.recover_cart_data,
        )
        // null indicates that not all of the data is ready yet
        if (cartRecoveredFromSharedCart === null) return

        if (
            cartRecoveredFromSharedCart ||
            !!getFromStorageParsed(CART_RECOVERED_KEY)
        ) {
            setCartRecovered(true)
            return
        }

        const recoveredCart = abandonmentStorefrontData.recover_cart_data
        let cartItems: CartItem[] = []
        if (isApiCart(recoveredCart)) {
            cartItems = convertApiCartToCartCallback(recoveredCart).items
        } else if (isAjaxShopifyCartData(recoveredCart)) {
            cartItems = recoveredCart.items.map((item) => ({
                variant:
                    findVariantWithImageForAjaxCartItemCallback(item) ??
                    convertAjaxToVariantCallback(item),
                quantity: item.quantity,
                sellingPlanId: item.selling_plan_allocation?.selling_plan.id
                    ? `gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}`
                    : undefined,
                // item.properties contains the attributes in a json object.
                attributes: item.properties,
            }))
        }
        if (cartItems.length > 0) {
            addCartItems(cartItems, false)
                .then(() => {
                    setCartAsRecovered()
                })
                .catch((error) => {
                    console.error(error)
                })
            return
        }
        setCartAsRecovered()
    }

    const updateCartItem = (
        variant: VariantWithImage,
        quantity?: number,
        sellingPlanId?: string | null,
        immediateCheckout: boolean = false,
        shopPay?: boolean,
        // Identifies which of the variant's lines to update when the same
        // variant is in the cart both with and without a selling plan.
        currentSellingPlanId?: string | null,
        shopifyLineId?: string,
    ) => {
        const existingItem = getItem(
            variant,
            currentSellingPlanId,
            shopifyLineId,
        )
        if (existingItem) {
            const previousQuantity = existingItem.quantity
            const newQuantity = quantity ?? existingItem.quantity
            existingItem.quantity = newQuantity
            // undefined means "leave the plan as is" (e.g. quantity changes).
            if (sellingPlanId !== undefined) {
                // Switching plans: if another line already has the target
                // plan, fold this line into it instead of leaving two lines
                // with the same variant and plan. Only possible once both
                // lines are synced; otherwise convert in place and let
                // Shopify consolidate.
                const targetItem = getItem(variant, sellingPlanId ?? null)
                if (
                    targetItem &&
                    targetItem !== existingItem &&
                    hasShopifyCartLineId(targetItem) &&
                    hasShopifyCartLineId(existingItem)
                ) {
                    targetItem.quantity += existingItem.quantity
                    queueUpdateCartLineItemsTask(
                        [targetItem],
                        immediateCheckout,
                        undefined,
                        shopPay,
                    )
                    queueRemoveCartLineItemsTask([existingItem], false)
                    return
                }
                existingItem.sellingPlanId = sellingPlanId
            }
            trackCartQuantityDeltaIfNeeded(
                Analytics,
                variant,
                previousQuantity,
                newQuantity,
                sellingPlanId,
            )
            // cartLinesUpdate requires a Shopify line id; optimistic lines
            // only get a local quantity/plan change until add completes.
            if (!hasShopifyCartLineId(existingItem)) {
                setCart((prevCart) => {
                    const newCart = cloneCart(prevCart)
                    const item = findCartItem(newCart.items, variant, {
                        sellingPlanId: currentSellingPlanId,
                        shopifyLineId,
                    })
                    if (item) {
                        item.quantity = existingItem.quantity
                        item.sellingPlanId = existingItem.sellingPlanId
                    }
                    return newCart
                })
                return
            }
            queueUpdateCartLineItemsTask(
                [existingItem],
                immediateCheckout,
                undefined,
                shopPay,
            )
        } else {
            addCartItem(
                variant,
                quantity,
                sellingPlanId,
                immediateCheckout,
                shopPay,
            )
        }
    }

    const removeCartItem = (
        variant: VariantWithImage,
        sellingPlanId?: string | null,
        shopifyLineId?: string,
    ) => {
        const existingItem = getItem(variant, sellingPlanId, shopifyLineId)

        if (!existingItem) {
            console.error(
                'Item not found in cart: ',
                JSON.stringify(variant, null, 2),
            )
            return
        }

        Analytics.TRACK_CART_REMOVE_ITEM(variant, existingItem.quantity)

        // If the item is not really in the cart, we can just remove it without
        // going to the DB.
        if (
            !existingItem.shopifyLineId &&
            (existingItem.status === CartItemStatus.READY ||
                !existingItem.status)
        ) {
            const removedSellingPlanId = existingItem.sellingPlanId ?? null
            setCart((prevCart) => {
                const newCart = cloneCart(prevCart)
                newCart.items = prevCart.items.filter(
                    (item) =>
                        item.variant.shopifyId !== variant.shopifyId ||
                        (item.sellingPlanId ?? null) !== removedSellingPlanId,
                )
                return newCart
            })
            return
        }

        queueRemoveCartLineItemsTask([existingItem], false)
    }

    const onCheckout = useCallback(
        (shopPay?: boolean) => {
            _onCheckout(cart, shopPay)
        },
        [_onCheckout, cart],
    )

    const getCartTotal = useCallback(() => {
        const fixedDiscount = getEffectiveFixedDiscountForCart(
            cart,
            landingPage.discount ?? null,
        )
        return getDiscountedCartTotal(cart, true, fixedDiscount)
    }, [cart, landingPage.discount])

    const getCartSubtotal = useCallback(() => {
        // Ignore the cart.subtotal for now.
        // We need to make sure we're using the original list price which may not be
        // accurately represented in the cart.subtotal.
        return getUnDiscountedCartTotal(cart, false, {
            landingPageLinks: resolvedLandingPageLinksRef.current,
            discount: landingPage.discount,
        })
    }, [cart, landingPage.discount])

    return (
        <CartContext.Provider
            value={{
                cart,
                updateCartItem,
                removeCartItem,
                addCartItem,
                onCheckout,
                addCartItems,
                getCartSubtotal,
                getCartTotal,
            }}
        >
            {children}
        </CartContext.Provider>
    )
}
